import { QueryObserverOptions } from '@tanstack/react-query';
import { DateCondition, Filter } from '../filters/filter.types';
import { AliasResolution } from '../types';

type QueryObjectTagValue = number | string | boolean;
type QueryObjectKnownTags = {
  widgetId: string;
};
export type QueryObjectTags = Partial<QueryObjectKnownTags & Record<string, QueryObjectTagValue>>;
export type QueryObjectProps = {
  queryObject: DwQueryRequest | DwQueryRequest[];
  origin?: string;
  showTotals?: boolean;
  totalRowCount?: boolean;
  onError?: (error) => void;
  options?: Partial<QueryObserverOptions>;
  muteErrors?: boolean;
  contextFilter?: Filter;
  debounceQueries?: boolean;
  searchQuery?: string;
  fileFormat?: string;
  aliasResolution?: AliasResolution;
  tags?: QueryObjectTags;
  featureFlagsOverrides?: FeatureFlagsOverrides;
};
export const DATE_TRUNC_FUNCTION_NAME = 'DATE_TRUNC' as const;
export const EXPLODE_FUNCTION_NAME = 'EXPLODE' as const;
export type UseQueryObjectProps = QueryObjectProps & { onSuccess?: (d: any[], index: number) => any[] };
export enum SortType {
  ASC = 'asc',
  DESC = 'desc',
}
export enum SortDir {
  ASC = 'ASC',
  DESC = 'DESC',
}

export const SortDirToSortType = {
  [SortDir.ASC]: SortType.ASC,
  [SortDir.DESC]: SortType.DESC,
} as const satisfies Record<SortDir, SortType>;

export const SortTypeToSortDir = {
  [SortType.ASC]: SortDir.ASC,
  [SortType.DESC]: SortDir.DESC,
} as const satisfies Record<SortType, SortDir>;

const NONE_OPTION = { title: 'None', value: 'none' };
export const DATE_BUCKET_OPTIONS = {
  none: NONE_OPTION,
  day: { title: 'Day', value: 'day' },
  week: { title: 'Week', value: 'week' },
  month: { title: 'Month', value: 'month' },
  year: { title: 'Year', value: 'year' },
} as const;

export type DateBucketValueType =
  | typeof DATE_BUCKET_OPTIONS.day.value
  | typeof DATE_BUCKET_OPTIONS.week.value
  | typeof DATE_BUCKET_OPTIONS.month.value
  | typeof DATE_BUCKET_OPTIONS.year.value;

export type DateTruncExpression = {
  function: {
    name: typeof DATE_TRUNC_FUNCTION_NAME;
    args: [
      {
        symbol: {
          ref: string;
        };
      },
      {
        stringLiteral: {
          value: DateBucketValueType;
        };
      },
    ];
  };
};

export type ExplodeExpression = {
  function: {
    name: typeof EXPLODE_FUNCTION_NAME;
    args: [
      {
        symbol: {
          ref: string;
        };
      },
    ];
  };
};

export type ExpressionTypes = DateTruncExpression | ExplodeExpression;

export interface Field {
  name: string;
  alias?: string;
  type?: string;
  expression?: ExpressionTypes;
}
export interface QueryObjectMetric extends Field {
  conf?: MetricConfiguration;
}
export interface MetricConfiguration {
  percentage?: { breakdown?: boolean };
  pivot?: {
    metrics: Field[];
    pivotDimension: Field;
    enableRowTotals: boolean;
    pivotFilter: {
      pivotDimApproxTopK: {
        k: number;
      };
    };
  };
}

interface Select {
  dims: Field[];
  metrics: QueryObjectMetric[];
}

export interface TopResults {
  size?: number;
  groupOthers: boolean;
  offset?: number;
}

type SortPath = {
  path: { name: string }[];
};

export interface SortBy {
  name: string;
  dir: SortDir;
  path?: SortPath;
}

export interface ProjectionID {
  name: string;
  builtIn: boolean;
}

export type FeatureFlagsOverrides = {
  binnedTimeBuckets?: boolean;
  queryAggIfCollapseOptimization?: boolean;
};

export interface DwQueryRequest {
  timezoneOffset?: number;
  timezone?: string;
  select: Select;
  filter?: Filter;
  groupBy?: string[];
  sorting: SortBy[];
  top?: TopResults;
  projectionId: ProjectionID;
  distinct?: boolean;
  timeRange?: DateCondition;
  metricsFilterThreshold?: number;
  featureFlagsOverrides?: FeatureFlagsOverrides;
  metricFilterRootProj?: boolean;
}
